import { supabase } from '../supabase';
import type { EmailLista } from '../../types/email.types';
import { aBlocchi } from './pubblico';

/**
 * Le liste di Mailing: crearle, rinominarle, eliminarle, e mettere o togliere
 * contatti.
 *
 * Nel database una lista e' un'etichetta (email_tags, email_contact_tags). Chi
 * puo' scriverci lo decidono le regole di accesso di quelle tabelle; che cosa
 * succede quando si elimina una lista usata da una sequenza lo decide il
 * database (migrazione 24), non questo file.
 */

// Stesso motivo di emailService: le tabelle email_* non sono ancora in
// database.types.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** Quante righe per richiesta, quando si spostano molti contatti insieme. */
const BLOCCO = 500;

/** Il codice con cui Postgres dice "esiste gia'". */
const DOPPIONE = '23505';

function erroreLeggibile(errore: { code?: string; message?: string }, nome: string): Error {
  if (errore.code === DOPPIONE) return new Error(`Esiste gia' una lista che si chiama "${nome}".`);
  return new Error(errore.message ?? 'Errore imprevisto.');
}

export const listeService = {
  async elenco(): Promise<EmailLista[]> {
    const { data, error } = await db.from('email_liste').select('*').order('name');
    if (error) throw error;
    return (data || []) as EmailLista[];
  },

  async crea(nome: string, descrizione?: string): Promise<string> {
    const pulito = nome.trim();
    const { data, error } = await db
      .from('email_tags')
      .insert({ name: pulito, description: descrizione?.trim() || null })
      .select('id')
      .single();
    if (error) throw erroreLeggibile(error, pulito);
    return data.id as string;
  },

  async modifica(id: string, nome: string, descrizione?: string): Promise<void> {
    const pulito = nome.trim();
    const { error } = await db
      .from('email_tags')
      .update({ name: pulito, description: descrizione?.trim() || null, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw erroreLeggibile(error, pulito);
  },

  /** Toglie la lista, non i contatti: restano in rubrica, solo non piu' raggruppati. */
  async elimina(id: string): Promise<void> {
    const { error } = await db.from('email_tags').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  /**
   * Mette dei contatti in una lista. Chi c'e' gia' resta com'e'.
   * Attenzione: entrare in una lista puo' far partire una sequenza che
   * aspetta proprio quella lista. E' voluto, ed e' scritto nella guida.
   */
  async aggiungi(listaId: string, contattiIds: readonly string[]): Promise<void> {
    for (const pezzo of aBlocchi([...new Set(contattiIds)], BLOCCO)) {
      const { error } = await db
        .from('email_contact_tags')
        .upsert(
          pezzo.map((contact_id) => ({ contact_id, tag_id: listaId })),
          { onConflict: 'contact_id,tag_id', ignoreDuplicates: true }
        );
      if (error) throw error;
    }
  },

  async togli(listaId: string, contattiIds: readonly string[]): Promise<void> {
    for (const pezzo of aBlocchi([...new Set(contattiIds)], BLOCCO)) {
      const { error } = await db
        .from('email_contact_tags')
        .delete()
        .eq('tag_id', listaId)
        .in('contact_id', pezzo);
      if (error) throw error;
    }
  },

  /**
   * Mette in una lista gli indirizzi appena importati o inseriti a mano.
   * L'importazione restituisce solo dei conteggi, quindi i contatti si
   * ritrovano dall'indirizzo. Torna quanti ne ha messi.
   */
  async aggiungiPerIndirizzo(listaId: string, indirizzi: readonly string[]): Promise<number> {
    const normalizzati = [...new Set(indirizzi.map((i) => i.trim().toLowerCase()).filter(Boolean))];
    const trovati: string[] = [];

    for (const pezzo of aBlocchi(normalizzati, BLOCCO)) {
      const { data, error } = await db
        .from('email_contacts')
        .select('id')
        .in('email_normalized', pezzo);
      if (error) throw error;
      trovati.push(...((data || []) as { id: string }[]).map((r) => r.id));
    }

    await listeService.aggiungi(listaId, trovati);
    return trovati.length;
  },

  /** Le liste in cui si trova un contatto. */
  async delContatto(contattoId: string): Promise<string[]> {
    const { data, error } = await db
      .from('email_contact_tags')
      .select('tag_id')
      .eq('contact_id', contattoId);
    if (error) throw error;
    return ((data || []) as { tag_id: string }[]).map((r) => r.tag_id);
  },
};
